// Theme constants matching Flutter app exactly

import { CoreController } from "@/services/coreController";

export const AppTheme = {
  // Primary Colors
  primaryColor: "#002366",
  greenColor: "#0052cc",
  amberColor: "#FF9800",
  errorColor: "#FF0000",
  surfaceColor: "#FFFFFF",
  backgroundColor: "#F8F8F8",

  // Gradients
  gradients: {
    "1XBET": {
      from: "#002366",
      to: "#008A44",
    },
    BETWINNER: {
      from: "#0052cc",
      to: "#002366",
    },
    DEFAULT: {
      from: "#002366",
      to: "#0052cc",
    },
  },

  // Text Colors
  textPrimary: "#181818",
  textSecondary: "#4a4a4a",
  textTertiary: "#757575",
  textWhite: "#FFFFFF",
  textError: "#FF0000",
  textSuccess: "#0052cc",
  textWarning: "#FF9800",
} as const;

export const AppDimensions = {
  // Spacing
  spacingControl: 4,
  spacingStandard: 8,
  spacingContainer: 16,
  spacingLarge: 32,

  // Button
  buttonHeight: 60,

  // Radius
  textRadius: 50,
  editTextRadius: 6,
  cardBorderRadius: 12,
  keyboardRadius: 40,

  // Elevation
  elevation: 6,
  borderWidth: 5,

  // Item Spacing
  itemSpacing: 22,
  defaultPadding: 16,
} as const;

export const AppTypography = {
  // Font Sizes
  textSizeSmall: 12,
  textSizeSMedium: 16,
  textSizeMedium: 16,
  textSizeLargeMedium: 18,
  textSizeNormal: 20,
  textSizeLarge: 24,
  textSizeXLarge: 30,

  // Font Families (matching Google Fonts usage)
  fontPrimary: "'Poppins', sans-serif",
  fontSecondary: "'Quicksand', sans-serif",
} as const;

export const AppConstants = {
  minAmount: 250,
  minWithdrawAmount: 900,
  maxAmount: 1000000,
  whatsappUrlDefault: "https://wa.me/22605756806",
  referralUrl: "https://mobcash-agent-dashboard.web.app/register",
  animationDuration: 300,

  // Bonus rates (EASYSERVICE rewards)
  depositBonusRate: 0.01, // 1% bonus on deposits
  withdrawBonusRate: 0.004, // 0.4% bonus on withdrawals
  // Les bonus ne sont accordés qu'au-delà de ce seuil
  bonusMinAmount: 1000,

  // WebFrame
  maxFrameWidth: 475,
  designWidth: 390,
  designHeight: 844,

  // Session
  sessionDuration: 180, // 3 minutes in seconds

  // Polling
  maxPollingAttempts: 36, // 3 minutes at 5 seconds interval
  pollingIntervalSeconds: 5,
} as const;

export const OperatorConfig = {
  orange: {
    prefixes: [
      "04",
      "05",
      "06",
      "07",
      "44",
      "54",
      "55",
      "56",
      "57",
      "64",
      "65",
      "66",
      "67",
      "74",
      "75",
      "76",
      "77",
    ] as readonly string[],
    ussdCode: (amount: number) => `*144*2*1*${CoreController.instance.appSettingResponse.config?.OM_PAYEMENT_NUMBER ?? "05690560"}*${amount}#`,
    color: "#FF7A00",
    bgColor: "#FFF4E6",
    name: "Orange Money",
  },
  moov: {
    prefixes: [
      "01",
      "02",
      "03",
      "50",
      "51",
      "52",
      "53",
      "60",
      "61",
      "62",
      "63",
      "70",
      "71",
      "72",
      "73",
    ] as readonly string[],
    ussdCode: (amount: number) => `*555*4*1*${CoreController.instance.appSettingResponse.config?.MOOV_PAYEMENT_NUMBER ?? "03301404"}*${amount}#`,
    color: "#0066CC",
    bgColor: "#EFF8FF",
    name: "Moov Money",
  },
} as const;

// Helper functions
export const getAccountColor = (accountType: string | undefined): string => {
  switch (accountType?.toUpperCase()) {
    case "1XBET":
      return AppTheme.primaryColor;
    case "BETWINNER":
      return AppTheme.greenColor;
    default:
      return AppTheme.amberColor;
  }
};

export const getAccountGradient = (accountType: string | undefined) => {
  switch (accountType?.toUpperCase()) {
    case "1XBET":
      return AppTheme.gradients["1XBET"];
    case "BETWINNER":
      return AppTheme.gradients["BETWINNER"];
    default:
      return AppTheme.gradients["DEFAULT"];
  }
};

export const getOperatorFromPrefix = (
  prefix: string
): "orange" | "moov" | null => {
  if (OperatorConfig.orange.prefixes.includes(prefix)) {
    return "orange";
  } else if (OperatorConfig.moov.prefixes.includes(prefix)) {
    return "moov";
  }
  return null;
};

export const formatAmount = (amount: number | string): string => {
  const num = typeof amount === "string" ? parseInt(amount) : amount;
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

export const calculateBonus = (
  amount: number | string,
  type: "deposit" | "withdraw",
  factor?: number
): number => {
  const num = typeof amount === "string" ? parseInt(amount) : amount;
  if (num < AppConstants.bonusMinAmount) return 0;

  // Directus stores 1.04 for 4% bonus, 1 for 0%. Rate = factor - 1.
  const resolvedFactor = factor ?? (type === "deposit" ? AppConstants.depositBonusRate + 1 : AppConstants.withdrawBonusRate + 1);
  const rate = resolvedFactor >= 1 ? resolvedFactor - 1 : resolvedFactor;
  return Math.round(num * rate);
};

export const calculateTotalWithBonus = (
  amount: number | string,
  type: "deposit" | "withdraw",
  factor?: number
): number => {
  const num = typeof amount === "string" ? parseInt(amount) : amount;
  return num + calculateBonus(num, type, factor);
};
